import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form, Divider, Button } from 'semantic';
import InputField from 'components/form-fields/formik/InputField';
import { Formik } from 'formik';
import { useUser } from 'contexts/user';
import { useMutation } from '@tanstack/react-query';
import { request } from 'utils/api';

export type FieldServiceDetailsFields = {
  fieldServiceEmail: string;
};

export default function FieldServiceDetails(props: { onSubmit: () => void }) {
  const { user } = useUser();
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    fieldServiceEmail: Yup.string()
      .email(t('requestCardModel.emailInvalid', 'Invalid email address'))
      .required(t('requestCardModel.emailInvalid', 'Invalid email address')),
  });

  if (!user) {
    return null;
  }

  const finalizeDetails = useMutation<
    unknown,
    Error,
    FieldServiceDetailsFields
  >({
    mutationFn: (body: FieldServiceDetailsFields) => {
      return request({
        method: 'PATCH',
        path: `/1/accounts/self`,
        body: {
          fieldService: {
            email: body.fieldServiceEmail,
          },
        },
      });
    },
    onSuccess: () => {
      return props.onSubmit();
    },
  });

  return (
    <div>
      <Formik<FieldServiceDetailsFields>
        initialValues={{
          fieldServiceEmail: user.email || '',
        }}
        validationSchema={schema}
        validateOnBlur
        validateOnChange
        onSubmit={async (values: FieldServiceDetailsFields) => {
          finalizeDetails.mutate(values);
        }}>
        {({ handleSubmit, isSubmitting, isValid }) => (
          <Form>
            <Form.Field>
              <InputField
                name="fieldServiceEmail"
                label={t(
                  'fieldServiceDetails.evseIssuesEmail',
                  'EVSE Issues Email'
                )}
              />
              <p>
                {t(
                  'fieldServiceDetails.evseIssuesEmailDescription',
                  'Email address to receive notifications about issues with your EVSEs.'
                )}
              </p>
            </Form.Field>

            <Divider hidden />
            <Button
              style={{
                margin: 0,
              }}
              onClick={handleSubmit}
              disabled={isSubmitting || !isValid}
              loading={isSubmitting}
              primary
              size="large"
              content={t('common.continue', 'Continue')}
              fluid
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}
